/**
 * Organization Change Management Module
 *
 * This module handles the cross-tab coordination during organization switching
 * to prevent users from being unintentionally logged out.
 *
 * Problem:
 * When a user switches between organizations (SCACs) in one browser tab,
 * other open tabs may encounter authentication issues because:
 * 1. The auth token is updated for the new organization in localStorage (shared across tabs)
 * 2. Other tabs still have pages/components tied to the previous organization
 * 3. This mismatch causes 401 errors when those tabs make API requests with the new token
 * 4. Our global error handler logs users out on 401 errors, causing a poor user experience
 *
 * Solution:
 * 1. Track when an org change is in progress using localStorage with a timestamp
 * 2. Prevent logout on 401 errors during this transition period
 * 3. Broadcast to all tabs when an org change occurs to trigger page reloads
 * 4. Clean up stale state to prevent indefinite suppression of authentication errors
 */

const ORG_CHANGE_STATE_START_TIME_KEY = 'orgChangeStateStartTime'
const AUTH_CHANNEL_NAME = 'auth-channel'
const ORG_CHANGE_TIMEOUT = 60 * 1000 // 1 minute in milliseconds
const ORG_CHANGE_MESSAGE_TYPE = 'orgChange'

// New helper: Get a unique ID for this tab (stored in sessionStorage)
function getTabId(): string {
  let tabId = sessionStorage.getItem('tabId')
  if (!tabId) {
    // Generate a new random ID combining current time and a random factor
    tabId = Date.now().toString() + Math.random().toString().substring(2)
    sessionStorage.setItem('tabId', tabId)
  }
  return tabId
}

/**
 * Clears the organization change state from localStorage
 * Called when organization change is complete or on cleanup
 */
export function clearOrgChangeState() {
  localStorage.removeItem(ORG_CHANGE_STATE_START_TIME_KEY)
}

/**
 * Sets the organization change state with current timestamp
 * Called at the beginning of an organization switch operation
 */
export function setOrgChangeState() {
  localStorage.setItem(ORG_CHANGE_STATE_START_TIME_KEY, Date.now().toString())
}

/**
 * Notifies all open tabs to reload after an organization change
 * This ensures all tabs switch to the new organization context
 */
export function broadcastOrgChangeToOtherTabs(newOrgId: number) {
  const channel = new BroadcastChannel(AUTH_CHANNEL_NAME)
  const senderId = getTabId()
  channel.postMessage({ type: ORG_CHANGE_MESSAGE_TYPE, senderId, newOrgId })
  channel.close()
}

/**
 * Creates a listener for organization change broadcasts from other tabs.
 * Only responds to reload messages not originating from this tab.
 * Returns the channel so it can be properly closed on component unmount.
 *
 * @returns {BroadcastChannel} The broadcast channel instance for cleanup
 */
export function onOrgChangedInOtherTab(
  callback: (newOrgId: number) => void
): BroadcastChannel {
  const channel = new BroadcastChannel(AUTH_CHANNEL_NAME)
  const tabId = getTabId()
  channel.addEventListener('message', (event) => {
    // Only reload if the message type is 'reload' and the sender is not this tab.
    if (
      event.data.type === ORG_CHANGE_MESSAGE_TYPE &&
      event.data.senderId !== tabId
    ) {
      callback(event.data.newOrgId)
    }
  })
  return channel
}

/**
 * Checks if there is an active organization change in progress
 * Used by error handlers to determine whether to suppress logout on 401 errors
 *
 * @returns {boolean} True if an org change is active (less than 1 minute old)
 */
export function isThereAnActiveOrgChange(): boolean {
  const orgChangeStateStartTimeStr = localStorage.getItem(
    ORG_CHANGE_STATE_START_TIME_KEY
  )
  if (orgChangeStateStartTimeStr) {
    const orgChangeStateStartTime = Number.parseInt(
      orgChangeStateStartTimeStr,
      10
    )
    const now = Date.now()
    // If org change started less than 1 minute ago, consider it active
    if (
      !Number.isNaN(orgChangeStateStartTime) &&
      now - orgChangeStateStartTime < ORG_CHANGE_TIMEOUT
    ) {
      return true
    }
  }
  return false
}
